import React from 'react';
import { AncientOneSelectionType, InvestigatorDrawType } from '../api';
import { ErrorMessage } from './ErrorMessage';
import { ExpansionToggles } from './ExpansionToggles';
import { RandomCluePlacementOption } from './RandomCluePlacementOption';

interface MultiplayerViewProps {
  playersCount: number;
  maxInvestigatorsCount: number;
  seed: string;
  investigatorDrawType: InvestigatorDrawType;
  ancientOneSelectionType: AncientOneSelectionType;
  enableDunwichBoard?: boolean;
  enableKingsportBoard?: boolean;
  enableInnsmouthBoard?: boolean;
  onEnableDunwichBoardChange?: (value: boolean) => void;
  onEnableKingsportBoardChange?: (value: boolean) => void;
  onEnableInnsmouthBoardChange?: (value: boolean) => void;
  includeDunwichCards: boolean;
  includeDarkPharaohCards: boolean;
  includeKingsportCards: boolean;
  includeKingInYellowCards: boolean;
  includeBlackGoatCards: boolean;
  includeMiskatonicCards?: boolean;
  includeLurkerCards?: boolean;
  includeInsmouthCards: boolean;
  usePersonalStories: boolean;
  useNewGateMarkers?: boolean;
  includeFanMadeInvestigators: boolean;
  randomCluePlacement: boolean;
  showAdminOnly?: boolean;
  loading: boolean;
  error?: string | null;
  onPlayersCountChange: (value: number) => void;
  onInvestigatorsCountChange: (value: number) => void;
  onSeedChange: (value: string) => void;
  onDrawTypeChange: (value: InvestigatorDrawType) => void;
  onAncientOneSelectionTypeChange: (value: AncientOneSelectionType) => void;
  onIncludeDunwichCardsChange: (value: boolean) => void;
  onIncludeDarkPharaohCardsChange: (value: boolean) => void;
  onIncludeKingsportCardsChange: (value: boolean) => void;
  onIncludeKingInYellowCardsChange: (value: boolean) => void;
  onIncludeBlackGoatCardsChange: (value: boolean) => void;
  onIncludeMiskatonicCardsChange?: (value: boolean) => void;
  onIncludeLurkerCardsChange?: (value: boolean) => void;
  onIncludeInsmouthCardsChange: (value: boolean) => void;
  onUsePersonalStoriesChange: (value: boolean) => void;
  onUseNewGateMarkersChange?: (value: boolean) => void;
  onIncludeFanMadeInvestigatorsChange: (value: boolean) => void;
  onRandomCluePlacementChange: (value: boolean) => void;
  onCreate: () => void;
  onBack: () => void;
  isPrivate?: boolean;
  onPrivateChange?: (value: boolean) => void;
}

export const MultiplayerView: React.FC<MultiplayerViewProps> = ({
  playersCount,
  maxInvestigatorsCount,
  seed,
  isPrivate,
  investigatorDrawType,
  ancientOneSelectionType,
  enableDunwichBoard = true,
  enableKingsportBoard = true,
  enableInnsmouthBoard = true,
  onEnableDunwichBoardChange,
  onEnableKingsportBoardChange,
  onEnableInnsmouthBoardChange,
  includeDunwichCards,
  includeDarkPharaohCards,
  includeKingsportCards,
  includeKingInYellowCards,
  includeBlackGoatCards,
  includeMiskatonicCards = false,
  includeLurkerCards = false,
  includeInsmouthCards,
  usePersonalStories,
  useNewGateMarkers = true,
  includeFanMadeInvestigators,
  randomCluePlacement,
  showAdminOnly,
  loading,
  error,
  onPrivateChange,
  onPlayersCountChange,
  onInvestigatorsCountChange,
  onSeedChange,
  onDrawTypeChange,
  onAncientOneSelectionTypeChange,
  onIncludeDunwichCardsChange,
  onIncludeDarkPharaohCardsChange,
  onIncludeKingsportCardsChange,
  onIncludeKingInYellowCardsChange,
  onIncludeBlackGoatCardsChange,
  onIncludeMiskatonicCardsChange,
  onIncludeLurkerCardsChange,
  onIncludeInsmouthCardsChange,
  onUsePersonalStoriesChange,
  onUseNewGateMarkersChange,
  onIncludeFanMadeInvestigatorsChange,
  onRandomCluePlacementChange,
  onCreate,
  onBack,
}) => {
  return (
    <div className="App">
      <div className="solo-menu">
        <h2 id="multiplayer-setup-heading" tabIndex={-1}>Create multiplayer game <small>(play with other players)</small></h2>
        <label className='select-label'>
          Number of players:
          <select
            className="select-input"
            value={playersCount || 1}
            onChange={e => onPlayersCountChange(Number(e.target.value) || 1)}
          >
            <option value={1}>1</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={4}>4</option>
            <option value={5}>5</option>
            <option value={6}>6</option>
            <option value={7}>7</option>
            <option value={8}>8</option>
          </select>
        </label>
        <label className='select-label'>
          Number of investigators:
          <select
            className="select-input"
            value={maxInvestigatorsCount}
            onChange={e => onInvestigatorsCountChange(Number(e.target.value))}
            aria-describedby="multiplayer-investigator-count-help"
          >
            {Array.from({ length: 8 }, (_, index) => index + 1).map(count => (
              <option key={count} value={count} disabled={count < playersCount}>{count}</option>
            ))}
          </select>
        </label>
        <small id="multiplayer-investigator-count-help">Maximum investigators. Each player gets one; extra investigators are optional.</small>
        <label className='checkbox-label'>
          <input
            type="checkbox"
            checked={isPrivate}
            onChange={e => onPrivateChange && onPrivateChange(e.target.checked)}
          />
          Private game (only accessible via direct link)
        </label>
        {/* <label>
          Seed (optional):
          <input
            type="number"
            value={seed}
            onChange={e => onSeedChange(e.target.value)}
            placeholder="Random if empty"
          />
        </label> */}
        <label className='select-label'>
          Investigator draw:
          <select
            className="select-input"
            value={investigatorDrawType}
            onChange={e => onDrawTypeChange(e.target.value as InvestigatorDrawType)}
          >
            <option value="RandomDraft">Random draft (3 options)</option>
            <option value="Random">Random</option>
            <option value="Selection">Selection</option>
          </select>
        </label>
        <label className='select-label'>
          Ancient One draw:
          <select
            className="select-input"
            value={ancientOneSelectionType}
            onChange={e => onAncientOneSelectionTypeChange(e.target.value as AncientOneSelectionType)}
          >
            <option value="Random">Random</option>
            <option value="Selection">Selection</option>
          </select>
        </label>
        <RandomCluePlacementOption
          checked={randomCluePlacement}
          onChange={onRandomCluePlacementChange}
        />
        <ExpansionToggles
          includeBaseCards={true}
          enableDunwichBoard={enableDunwichBoard}
          enableKingsportBoard={enableKingsportBoard}
          enableInnsmouthBoard={enableInnsmouthBoard}
          onEnableDunwichBoardChange={onEnableDunwichBoardChange}
          onEnableKingsportBoardChange={onEnableKingsportBoardChange}
          onEnableInnsmouthBoardChange={onEnableInnsmouthBoardChange}
          includeDunwichCards={includeDunwichCards}
          includeDarkPharaohCards={includeDarkPharaohCards}
          includeKingsportCards={includeKingsportCards}
          includeKingInYellowCards={includeKingInYellowCards}
          includeBlackGoatCards={includeBlackGoatCards}
          includeMiskatonicCards={includeMiskatonicCards}
          includeLurkerCards={includeLurkerCards}
          includeInsmouthCards={includeInsmouthCards}
          includeFanMadeInvestigators={includeFanMadeInvestigators}
          disableBaseCards={true}
          showAdminOnly={showAdminOnly}
          onIncludeDunwichCardsChange={onIncludeDunwichCardsChange}
          onIncludeDarkPharaohCardsChange={onIncludeDarkPharaohCardsChange}
          onIncludeKingsportCardsChange={onIncludeKingsportCardsChange}
          onIncludeKingInYellowCardsChange={onIncludeKingInYellowCardsChange}
          onIncludeBlackGoatCardsChange={onIncludeBlackGoatCardsChange}
          onIncludeMiskatonicCardsChange={onIncludeMiskatonicCardsChange}
          onIncludeLurkerCardsChange={onIncludeLurkerCardsChange}
          onIncludeInsmouthCardsChange={onIncludeInsmouthCardsChange}
          onIncludeFanMadeInvestigatorsChange={onIncludeFanMadeInvestigatorsChange}
        />
        {includeInsmouthCards && (
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={usePersonalStories}
              onChange={event => onUsePersonalStoriesChange(event.target.checked)}
            />
            Use Personal Stories
          </label>
        )}
        {includeLurkerCards && (
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={useNewGateMarkers}
              onChange={event => onUseNewGateMarkersChange?.(event.target.checked)}
            />
            Use New Gate Markers
          </label>
        )}
        <div className="create-game-actions">
          <button onClick={onCreate} disabled={loading}>
            {loading ? 'Creating...' : 'Create Game'}
          </button>
          <button className='glass-button' onClick={onBack}>Back</button>
        </div>
        {error && <ErrorMessage error={error} />}
      </div>
    </div>
  );
};
